<template>
    <v-card flat color="transparent" class="w-100 align-self-start">
        <v-card-title>
            <div class="d-flex flex-column">
                <div class="d-flex align-center">
                    <span class="secondary--text">Assignments</span>
                </div>
            </div>
        </v-card-title>
        <v-card-text>
            <div v-if="!fetchingSpaceBundles">
                <div class="d-flex align-center">
                    <v-btn
                        v-if="isSpaceAdmin"
                        color="primary"
                        class="mr-2"
                        :disabled="isSpaceArchived"
                        :to="{
                            name: 'snapshot-distribute',
                            params: { oid: $route.params.oid, sid: $route.params.sid, iid: $route.params.iid, snid: $route.params.snid }
                        }">
                        <v-icon left>mdi-plus</v-icon>
                        Assignment
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-text-field
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense
                        placeholder="Filter assignments..."
                        v-model="search"
                        clearable
                        class="mr-2"></v-text-field>
                    <v-btn :loading="fetchingSpaceBundles" @click="$store.dispatch('spaceStore/fetchSpaceBundles', $route.params.sid)" icon>
                        <v-icon>refresh</v-icon>
                    </v-btn>
                </div>
                <v-data-table
                    :search="search"
                    item-key="bid"
                    :options="tableOptions"
                    :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                    :headers="headers"
                    v-model="selected"
                    :item-class="setRowClass"
                    :items="filteredBundlesByType"
                    class="mt-4">
                    <template v-slot:top="{ pagination, options, updateOptions }">
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </template>
                    <template v-slot:no-data>
                        <div class="px-4 py-6">
                            <p class="mb-1"><b>No assignments found.</b></p>
                            <p class="mb-1">So far no assignments have been created.</p>
                            <p v-if="isSpaceAdmin" class="mb-3">
                                You can create new assignments by staging and sharing objects (files, tables, and applications) as an assignment.
                            </p>
                            <v-btn
                                v-if="isSpaceAdmin"
                                href="https://docs.nuvolos.com/how-to-guides/workflows-for-instructors/setting-assignments"
                                target="_blank"
                                outlined
                                small
                                color="info">
                                Learn more
                            </v-btn>
                        </div>
                    </template>
                    <template v-slot:[`item.long_id`]="{ item }">
                        <v-card flat color="transparent" class="my-1 d-inline-flex">
                            <v-list-item
                                :two-line="!!item.description"
                                :disabled="isSpaceArchived"
                                class="rounded overflow-hidden"
                                @click="!isSpaceArchived && gotoAssignment(item.bid)">
                                <v-list-item-content class="d-inline-block">
                                    <v-list-item-title>
                                        <b>{{ item.long_id }}</b>
                                    </v-list-item-title>
                                    <v-list-item-subtitle v-if="item.description">
                                        {{ item.description }}
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action v-if="!isSpaceArchived">
                                    <v-icon right color="primary" class="showOnHover">mdi-chevron-right</v-icon>
                                </v-list-item-action>
                            </v-list-item>
                        </v-card>
                    </template>
                    <template v-slot:[`item.assignment_status`]="{ item }">
                        <v-chip small>{{ item.assignment_status }}</v-chip>
                    </template>
                    <template v-slot:[`item.submission_status`]="{ item }">
                        <v-chip small v-if="item.submission_status === 'No submissions'" color="orange">
                            {{ item.submission_status }}
                        </v-chip>
                        <v-chip v-else small color="success">{{ item.submission_status }}</v-chip>
                    </template>
                    <template v-slot:[`item.allow_handins_until`]="{ item }">
                        <div class="d-flex align-center">
                            <span class="subtitle-2" :class="handinDeadlineStyle(item.allow_handins_until).color">
                                {{ item.allow_handins_until | dateTimeToHuman }}
                            </span>
                        </div>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu left offset-x nudge-top="10" v-if="!isSpaceArchived">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" text color="secondary" small>
                                    <v-icon>more_horiz</v-icon>
                                </v-btn>
                            </template>
                            <v-list nav dense>
                                <TheSnapshotAssignmentSubmitDialog
                                    v-if="!isSpaceAdmin && !isAssignmentOverdue(item.allow_handins_until) && !isMasterInstance"
                                    :bundleData="item">
                                    <template v-slot:dialogactivator="{ on, attrs }">
                                        <v-list-item v-bind="attrs" v-on="on">
                                            <v-list-item-title class="secondary--text mr-5">Hand-in</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </TheSnapshotAssignmentSubmitDialog>
                                <BundleEditDialog v-if="isSpaceAdmin" :bundleData="item">
                                    <template v-slot:dialogactivator="{ on, attrs }">
                                        <v-list-item v-bind="attrs" v-on="on">
                                            <v-list-item-icon>
                                                <v-icon color="secondary">mdi-pencil-outline</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="secondary--text mr-5">Edit</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </BundleEditDialog>
                                <v-divider v-if="isSpaceAdmin" class="my-2"></v-divider>
                                <BundleDeleteDialog v-if="isSpaceAdmin" :bundleData="item">
                                    <template v-slot:dialogactivator="{ on, attrs }">
                                        <v-list-item v-bind="attrs" v-on="on">
                                            <v-list-item-icon>
                                                <v-icon color="error">mdi-trash-can-outline</v-icon>
                                            </v-list-item-icon>
                                            <v-list-item-title class="error--text mr-5">Delete</v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </BundleDeleteDialog>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </div>
            <div v-else-if="fetchingSpaceBundles">
                <div
                    v-for="item in [
                        { id: 'c1', opacity: 1 },
                        { id: 'c2', opacity: 0.75 },
                        { id: 'c3', opacity: 0.5 }
                    ]"
                    :key="item.id"
                    class="my-6">
                    <div :style="{ opacity: item.opacity }">
                        <v-skeleton-loader type="list-item-avatar-two-line" class="mx-auto" />
                    </div>
                </div>
            </div>
        </v-card-text>
    </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { assignmentMethods } from '@/mixins/assignments'
import { sortDateArray, calculateDateDifferenceInDays } from '@/utils'

const BundleDeleteDialog = () => import('../components/TheSnapshotBundleDeleteDialog.vue')
const BundleEditDialog = () => import('../components/TheSnapshotBundleEditDialog')
const TheSnapshotAssignmentSubmitDialog = () => import('../components/TheSnapshotAssignmentSubmitDialog')

export default {
    name: 'SnapshotAssignments',
    components: {
        BundleDeleteDialog,
        TheSnapshotAssignmentSubmitDialog,
        BundleEditDialog
    },
    mixins: [assignmentMethods],
    data() {
        return {
            distributionReasons: {
                MATERIAL_SHARING: 'DISTRIBUTION',
                ASSIGNMENT: 'ASSIGNMENT'
            },
            headers: [
                { text: 'Name / Description', value: 'long_id', align: 'left' },
                {
                    text: 'Assignment status',
                    value: 'assignment_status',
                    align: 'left'
                },
                { text: 'Submission status', value: 'submission_status', align: 'left' },
                { text: 'Hand-in deadline', value: 'allow_handins_until', align: 'left' },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false }
            ],
            selected: [],
            search: '',
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            }
        }
    },
    computed: {
        ...mapState('spaceStore', ['spaceBundles', 'fetchingSpaceBundles']),
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'assignmentsExist', 'isSpaceRested', 'isSpaceArchived']),
        ...mapGetters('instanceStore', ['isMasterInstance']),
        filteredBundlesByType() {
            const filteredBundles = this.spaceBundles.filter(bundle => bundle.bundle_type_name === this.distributionReasons.ASSIGNMENT)
            filteredBundles.forEach(bundle => {
                if (!this.isAssignmentOverdue(bundle.allow_handins_until)) {
                    bundle.assignment_status = 'Open'
                } else if (this.isAssignmentOverdue(bundle.allow_handins_until) && !bundle.handbacks_visible) {
                    bundle.assignment_status = 'Grading'
                } else {
                    bundle.assignment_status = 'Grades visible'
                }
                if (bundle.handins && bundle.handins.length > 0) {
                    bundle.submission_status = `${this.gradeableHandinsSum(bundle)} gradeable submission(s)`
                } else {
                    bundle.submission_status = 'No submissions'
                }
            })
            return sortDateArray(filteredBundles, 'allow_handins_until', 'descending')
        }
    },
    methods: {
        setRowClass() {
            if (this.isSpaceArchived) {
                return 'opacity-7'
            }
        },
        handinDeadlineStyle(deadline) {
            const dayDiff = calculateDateDifferenceInDays(new Date(), deadline)
            if (dayDiff > 7) {
                return { color: 'green--text text--lighten-1' }
            } else if (dayDiff > 1) {
                return { color: 'orange--text text--darken-1' }
            } else if (dayDiff > 0) {
                return { color: 'orange--text text--darken-1' }
            } else if (dayDiff === 0) {
                return { color: 'red--text text--darken-2' }
            } else {
                return { color: 'secondary--text' }
            }
        },
        gradeableHandinsSum(assignment) {
            return assignment.handins.filter(handin => handin.is_latest).length
        }
    }
}
</script>
